import { useCallback, useEffect, useState } from 'react';
import type { BotArchetype } from '@goodtime/shared';
import { authHeaders, BASE } from '../api/client';

/**
 * U11 — client-side state for cast favourites + fan-counts.
 *
 * <p>Hydrates from {@code GET /v1/players/{playerId}/cast-favourites}
 * on mount. Toggle goes through {@code POST /v1/players/{playerId}/
 * cast-favourites/{archetype}} and updates local state from the
 * server response — no optimistic flip, because the heart-tap is a
 * single tap that produces a single network call, and the toggle
 * response carries the new state authoritatively.
 *
 * <p>Fan-counts load lazily — on first {@link #ensureFanCount} call
 * for an archetype, the hook GETs {@code /v1/cast/{archetype}/fan-
 * count} and caches the result. ProfileCard's open-on-avatar-tap
 * flow triggers this for one archetype at a time, so fan-count
 * traffic is at most 8 reads per session (one per cast member).
 *
 * <p>{@code playerId === null} → no-op idle state (the GET fires
 * nowhere, toggles are local-only and don't persist; useful for the
 * pre-server-connect dev surfaces).
 */

const API_BASE = BASE; // shared API base (includes /v1); resolves to the API origin in prod

export interface CastFavouritesState {
  /** Set of archetypes this player has favourited. */
  favourites: ReadonlySet<BotArchetype>;
  /** Cached fan-counts. Sparsely populated by {@link ensureFanCount}. */
  fanCounts: Readonly<Record<string, number>>;
  /** True while the initial GET of the favourites list is in flight. */
  loading: boolean;
  /** Most recent error message, or null. Cleared on the next successful op. */
  error: string | null;
  /** Toggle a (player, archetype) favourite. Resolves to the new state
   *  (true = now favourited, false = now not). */
  toggle: (archetype: BotArchetype) => Promise<boolean>;
  /** Ensure the fan-count for an archetype is loaded into the cache.
   *  No-op if already loaded. Returns the count. */
  ensureFanCount: (archetype: BotArchetype) => Promise<number>;
}

export function useCastFavourites(playerId: string | null): CastFavouritesState {
  const [favourites, setFavourites] = useState<ReadonlySet<BotArchetype>>(
    () => new Set(),
  );
  const [fanCounts, setFanCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState<boolean>(playerId !== null);
  const [error, setError] = useState<string | null>(null);

  // Initial load — fire when playerId arrives. Cancel-token via the
  // standard mounted-flag pattern so a fast unmount + remount doesn't
  // race two loads onto the same state.
  useEffect(() => {
    if (!playerId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/players/${encodeURIComponent(playerId)}/cast-favourites`,
          { headers: await authHeaders() },
        );
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const body = (await res.json()) as { favourites: BotArchetype[] };
        if (cancelled) return;
        setFavourites(new Set(body.favourites));
        setError(null);
      } catch (e) {
        if (cancelled) return;
        const message = e instanceof Error ? e.message : String(e);
        setError(`Couldn't load favourites — ${message}`);
        // Don't clear the cached set on error; stale data beats no data
        // for the heart-fill UX.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [playerId]);

  const toggle = useCallback(
    async (archetype: BotArchetype): Promise<boolean> => {
      if (!playerId) {
        // Idle / pre-connect: do an in-memory toggle so the UI still
        // animates, but don't persist. Real persistence kicks in
        // once {@code useServerConnection} resolves a playerId.
        let outcome = false;
        setFavourites((prev) => {
          const next = new Set(prev);
          if (next.has(archetype)) {
            next.delete(archetype);
            outcome = false;
          } else {
            next.add(archetype);
            outcome = true;
          }
          return next;
        });
        return outcome;
      }
      try {
        const res = await fetch(
          `${API_BASE}/players/${encodeURIComponent(playerId)}/cast-favourites/${encodeURIComponent(archetype)}`,
          { method: 'POST', headers: await authHeaders() },
        );
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const body = (await res.json()) as { favourited: boolean };
        setFavourites((prev) => {
          const next = new Set(prev);
          if (body.favourited) next.add(archetype);
          else next.delete(archetype);
          return next;
        });
        // The toggle moved the fan-count by 1; reflect it eagerly so
        // the ProfileCard chip doesn't lag a beat.
        setFanCounts((prev) => {
          const cur = prev[archetype];
          if (cur === undefined) return prev; // not loaded yet — ignore
          return { ...prev, [archetype]: cur + (body.favourited ? 1 : -1) };
        });
        setError(null);
        return body.favourited;
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        setError(`Couldn't update favourite — ${message}`);
        throw e;
      }
    },
    [playerId],
  );

  const ensureFanCount = useCallback(
    async (archetype: BotArchetype): Promise<number> => {
      const cached = fanCounts[archetype];
      if (cached !== undefined) return cached;
      try {
        const res = await fetch(
          `${API_BASE}/cast/${encodeURIComponent(archetype)}/fan-count`,
        );
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const body = (await res.json()) as { count: number };
        setFanCounts((prev) => ({ ...prev, [archetype]: body.count }));
        return body.count;
      } catch (e) {
        // On error, return 0 — the chip showing "0 fans" is less
        // alarming than a missing chip, and the error stays in
        // state for hosts that want to surface it.
        const message = e instanceof Error ? e.message : String(e);
        setError(`Couldn't load fan count — ${message}`);
        return 0;
      }
    },
    [fanCounts],
  );

  return { favourites, fanCounts, loading, error, toggle, ensureFanCount };
}
